/**
 * File Inspector Utility
 *
 * Simple password-protected file detection for Office and PDF files.
 * Supports DOCX, XLSX, PPTX, PDF, DOC, XLS, PPT file formats.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Convert Base64 to ArrayBuffer
function base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++)
        bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
}
// Simple PDF scan for /Encrypt token
function isPdfEncrypted(ab) {
    if (!ab || ab.byteLength < 4)
        return false;
    const headStr = new TextDecoder().decode(ab.slice(0, 16));
    if (!headStr.startsWith('%PDF-'))
        return false;
    const scanLen = Math.min(ab.byteLength, 256 * 1024); // 256 KB scan
    const content = new TextDecoder().decode(ab.slice(0, scanLen));
    return content.includes('/Encrypt');
}
// Simple Office file scan
function isOfficeEncrypted(ab) {
    return __awaiter(this, void 0, void 0, function* () {
        const header = new Uint8Array(ab.slice(0, 8));
        const zipSig = [0x50, 0x4B, 0x03, 0x04];
        const oleSig = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];
        const matchSig = (sig) => sig.every((v, i) => header[i] === v);
        // OOXML (ZIP) files
        if (matchSig(zipSig)) {
            try {
                const JSZip = (yield import('jszip')).default;
                const zip = yield JSZip.loadAsync(ab);
                // Check for encryption streams
                const fileNames = Object.keys(zip.files);
                return fileNames.some(name => name.toLowerCase().includes('encryptedpackage') ||
                    name.toLowerCase().includes('encryptioninfo'));
            }
            catch (error) {
                // Import DebugService dynamically to avoid circular dependencies
                const DebugService = require('../service/DebugService').default;
                DebugService.warn('[FileInspector] Error parsing ZIP file');
                return false;
            }
        }
        // Legacy OLE files
        if (matchSig(oleSig)) {
            try {
                const CFB = (yield import('cfb')).default;
                const cfb = CFB.read(new Uint8Array(ab), { type: "array" });
                const names = cfb.FullPaths.map(n => n.toLowerCase());
                // Check for encryption streams - this is the most reliable indicator
                return names.some(name => name.includes('/encryptedpackage') ||
                    name.includes('/encryptioninfo'));
            }
            catch (error) {
                // Import DebugService dynamically to avoid circular dependencies
                const DebugService = require('../service/DebugService').default;
                DebugService.warn('[FileInspector] Error parsing OLE file');
                return false;
            }
        }
        return false;
    });
}
/**
 * Get file extension from filename
 */
export function getFileExtension(filename) {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
        return '';
    }
    return filename.substring(lastDotIndex + 1).toLowerCase();
}
/**
 * Check if file type is supported for password protection detection
 */
export function isSupportedFileType(filename) {
    const extension = getFileExtension(filename);
    const supportedExtensions = ['docx', 'xlsx', 'pptx', 'pdf', 'doc', 'xls', 'ppt'];
    return supportedExtensions.includes(extension);
}
/**
 * Simple password protection detection
 */
export function detectFileProtectionFromBase64(base64) {
    return __awaiter(this, void 0, void 0, function* () {
        const ab = base64ToArrayBuffer(base64);
        // Check if it's a PDF
        if (isPdfEncrypted(ab)) {
            return {
                fileName: "unknown.pdf",
                type: "PDF",
                encrypted: true,
                details: ["PDF is encrypted"]
            };
        }
        // Check if it's an Office file
        const isEncrypted = yield isOfficeEncrypted(ab);
        if (isEncrypted) {
            return {
                fileName: "unknown.office",
                type: "Office file",
                encrypted: true,
                details: ["Office file is encrypted"]
            };
        }
        return {
            fileName: "unknown",
            type: "Unknown",
            encrypted: false,
            details: ["No encryption detected"]
        };
    });
}
